import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import { forgotPassword } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Field, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function ForgotPasswordPage() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("forgot.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-sm px-4 py-10 sm:py-16">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("forgot.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <p className="text-sm text-foreground">{t("forgot.sent")}</p>
              <p className="text-xs text-muted-foreground">{t("forgot.devNote")}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("forgot.intro")}</p>
              <Field label={t("auth.email")}>
                <TextInput
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? t("forgot.busy") : t("forgot.submit")}
              </Button>
            </form>
          )}
          <p className="mt-6 text-sm text-muted-foreground">
            {t("forgot.remembered")}{" "}
            <Link href="/login" className="font-medium text-primary transition-colors hover:underline">
              {t("common.logIn")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
